import { ExcelService } from "./excel.service";
import { ribbonService } from "./ribbon.service";
import {
  createStatusItems,
  deleteStatusItem,
  getStatusItemsBySpreadsheet,
  StatusItemDto,
} from "./status-items.service";
import { isNonWorkpaperSheet } from "./non-workpaper-sheets";
import { isStandaloneMode } from "../utils/standalone-mode";

/**
 * Sync status items with the current set of worksheets in the workbook.
 *
 * - Reads the live worksheet list from Excel.
 * - Creates a status item for any worksheet that doesn't have one yet
 *   (linked by the persistent worksheet ID, not the name).
 * - Deletes status items whose worksheet no longer exists.
 * - Refreshes the ribbon tab colours + status indicator to reflect the
 *   new state.
 * - Returns the updated status-item list.
 *
 * Called from the Status Items page's manual "Sync Excel" button AND
 * from the Setup page's Save flow — the latter fixes the recurring
 * "sign-off certificate is missing sheets because the user forgot to
 * sync first" incident.
 *
 * No-op in standalone (no Excel to read from) — returns whatever the
 * backend already has.
 */
export async function syncWorksheetsToStatusItems(spreadsheetId: string): Promise<StatusItemDto[]> {
  if (isStandaloneMode()) {
    return await getStatusItemsBySpreadsheet(spreadsheetId);
  }

  // Two lists, used for two different things — see the orphan pass below
  // for why conflating them would be destructive.
  //
  //   allWorksheets      — everything currently in the workbook.
  //   syncableWorksheets — just the workpapers. WFI's own sheets
  //                        (Completion Certificate, WI_Index, and the
  //                        XERO / XERO LY / XERO TB imports) are generated
  //                        or imported by WFI, so nobody prepares or
  //                        reviews them and they shouldn't be tracked.
  const allWorksheets = await ExcelService.getAllWorksheetsInfo();
  const syncableWorksheets = allWorksheets.filter((ws) => !isNonWorkpaperSheet(ws.name));
  const existingItems = await getStatusItemsBySpreadsheet(spreadsheetId);

  // Sheets without a status item yet.
  const existingSheetIds = new Set(
    existingItems.map((i) => i.sheetId).filter((s): s is string => !!s),
  );
  const sheetsToCreate = syncableWorksheets.filter((ws) => !existingSheetIds.has(ws.id));

  // Status items whose worksheet no longer exists.
  //
  // Deliberately keyed off allWorksheets, NOT syncableWorksheets: "we don't
  // create an item for this sheet" must not mean "destroy the item this
  // sheet already has". Filtering here instead would make every sync hard-
  // delete the existing status items for the XERO sheets (and the
  // certificate / index) — taking their discussion links with them, since
  // the backend delete is a real DELETE, not a soft one. Excluded sheets
  // therefore keep any item they already have; they just never gain a new
  // one. An excluded sheet that is genuinely removed from the workbook
  // drops out of allWorksheets too, so its item is still cleaned up.
  const worksheetIdSet = new Set(allWorksheets.map((ws) => ws.id));
  const itemsToDelete = existingItems.filter(
    (item) => item.sheetId && !worksheetIdSet.has(item.sheetId),
  );

  if (sheetsToCreate.length > 0) {
    await createStatusItems({
      spreadsheetId,
      items: sheetsToCreate.map((ws) => ({
        name: ws.name,
        sheetId: ws.id,
        type: "workflow_item",
        status: "Open",
      })),
    });
  }

  for (const item of itemsToDelete) {
    await deleteStatusItem(item.id);
  }

  // Ribbon side effects — self-gated on isStandaloneMode so safe to call.
  await ribbonService.syncAllWorksheetTabColors();
  await ribbonService.updateRibbonStatusIndicator();

  // Re-fetch so the returned list reflects the new + deleted rows.
  return await getStatusItemsBySpreadsheet(spreadsheetId);
}
